import { Box, Stack, Paper, IconButton, styled, ButtonBase } from '@mui/material'; // Paper kept for Header elevation default
import { Link as RouterLink } from 'react-router-dom';

export type UINavigationLinkProps = {
    selected?: boolean;
}

// Aug 2026 - header modernization. Kept the app's actual established accent (#0891B2, used
// everywhere else - buttons, links, badges across every other page) rather than introducing a
// separate new color just for the header - an early pass tried a deeper teal here, but it read as
// mismatched the moment you moved from the header into any page content still using the original
// cyan. Centralized here so every control in this file (and OrgSwitcher.tsx, which intentionally
// mirrors this file's pill language) pulls from the same two values instead of each hand-rolling
// its own hex.
export const HEADER_ACCENT = '#0891B2';
export const HEADER_ACCENT_HOVER = '#0E7490';

const ui = {
    Header: styled(Paper)(({ theme }) => ({
        width: '100%',
        height: '69px',
        borderRadius: 0,
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E2E8F0',
        boxShadow: '0px 2px 8px rgba(13,27,42,0.06)',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        display: 'flex',
        flexDirection: 'row',
        // stretch (not center) so NavigationContainer/NavigationItem can fill the full header
        // height - that's what lets the active tab's underline sit flush with the header's own
        // bottom edge instead of floating mid-row. Every other direct child that should stay a
        // vertically-centered pill (logo, buttons, user button) gets its own alignSelf: 'center'.
        alignItems: 'stretch',
        flexShrink: 0,
        boxSizing: 'border-box',
    })),

    LogoContainer: styled(RouterLink)(({ theme }) => ({
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        paddingRight: theme.spacing(3),
        marginRight: theme.spacing(3),
        borderRight: '1px solid #D8E4E8',
        height: '30px',
        transition: 'opacity 0.15s ease',
        '&:hover': { opacity: 0.75 },
    })),

    // minWidth: 0 is the fix for the actual overflow bug (not just a growing-item-count problem) -
    // a flex child with non-wrapping content defaults to a min-width equal to its own content
    // width, so once the primary links + "More" button were wider than the space left over, this
    // container refused to shrink and pushed the user button/name off past the header's own right
    // edge instead of truncating. overflow: hidden is the second half - without it, the refusal to
    // shrink just moves from this container's width to its content spilling out of it instead.
    NavigationContainer: styled(Stack)(({ theme }) => ({
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        alignItems: 'stretch',
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    })),

    NavigationItem: styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'stretch',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        flexShrink: 0,
    })),

    // Sentence case, icon-led, active state marked with a 2px underline flush with the header's
    // bottom edge (GitHub/Linear-style tab strip) instead of a plain color swap - reads as
    // "you are here" rather than just a slightly different shade of gray.
    NavigationLink: styled(RouterLink)<UINavigationLinkProps>(({ theme, selected }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        fontWeight: 500,
        fontSize: '13.5px',
        color: selected ? '#0D1B2A' : '#64748B',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        borderBottom: selected ? `2px solid ${HEADER_ACCENT}` : '2px solid transparent',
        transition: 'color 0.15s ease, border-color 0.15s ease',
        '& svg': { fontSize: '16px', color: selected ? HEADER_ACCENT : '#94A3B8', transition: 'color 0.15s ease' },
        '&:hover': {
            color: '#0D1B2A',
        },
        '&:hover svg': { color: HEADER_ACCENT },
        // Icon-only past a point narrower than the drawer breakpoint but wide enough that the
        // desktop nav is still showing - reclaims space for the primary links before anything has
        // to fall back to the hamburger drawer. Cutoff is 'xl', not 'lg' - with up to
        // PRIMARY_NAV_COUNT (5) items inline plus the org switcher/create/user button, 5 full-text
        // labels don't reliably fit until a genuinely wide (>=1536px) viewport; icon-only is the
        // safe default across ordinary laptop widths (1280-1440px) instead of risking overflow.
        [theme.breakpoints.down('xl')]: {
            '& > span': { display: 'none' },
        },
    })),

    // "More" trigger for every nav item beyond PRIMARY_NAV_COUNT (see Header.tsx) - keeps the
    // header's own width fixed regardless of how many features get added later, rather than
    // re-tuning breakpoints each time. Same
    // underline language as NavigationLink so it reads as one family of tabs, not a bolted-on menu.
    MoreButton: styled(ButtonBase)<UINavigationLinkProps>(({ selected }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 500,
        fontSize: '13.5px',
        color: selected ? '#0D1B2A' : '#64748B',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        borderBottom: selected ? `2px solid ${HEADER_ACCENT}` : '2px solid transparent',
        transition: 'color 0.15s ease, border-color 0.15s ease',
        '& svg': { fontSize: '16px', color: selected ? HEADER_ACCENT : '#94A3B8', transition: 'color 0.15s ease' },
        '&:hover': { color: '#0D1B2A' },
        '&:hover svg': { color: HEADER_ACCENT },
    })),

    // Moved here from the Orders page's action bar so "Create New Order" is reachable from any
    // page, next to the profile section. Aug 2026 - solid fill (not outlined) now that it's the
    // header's one clear primary action, matching the new accent everywhere else in this row.
    CreateOrderButton: styled(RouterLink)(({ theme }) => ({
        cursor: 'pointer',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'center',
        gap: '6px',
        justifyContent: 'center',
        borderRadius: '999px',
        backgroundColor: HEADER_ACCENT,
        border: `1px solid ${HEADER_ACCENT}`,
        color: '#FFFFFF',
        fontWeight: 600,
        padding: `0 ${theme.spacing(2)}`,
        height: '36px',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        flexShrink: 0,
        marginRight: theme.spacing(1.5),
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
            backgroundColor: HEADER_ACCENT_HOVER,
            borderColor: HEADER_ACCENT_HOVER,
            textDecoration: 'none',
        },
        [theme.breakpoints.down('md')]: { display: 'none' },
    })),

    SuspendedCreateOrderButton: styled(RouterLink)(({ theme }) => ({
        cursor: 'not-allowed',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'center',
        gap: '6px',
        justifyContent: 'center',
        borderRadius: '999px',
        backgroundColor: '#F3F4F6',
        border: '1px solid #D1D5DB',
        color: '#9CA3AF',
        fontWeight: 600,
        padding: `0 ${theme.spacing(2)}`,
        height: '36px',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        flexShrink: 0,
        marginRight: theme.spacing(1.5),
        '&:hover': { textDecoration: 'none' },
        [theme.breakpoints.down('md')]: { display: 'none' },
    })),

    // Divider between the Create Order action and the profile menu, grouping the two visually
    // without either one needing a heavy background/border to stand apart.
    ActionsDivider: styled(Box)(({ theme }) => ({
        width: '1px',
        height: '26px',
        alignSelf: 'center',
        backgroundColor: '#E2E8F0',
        marginRight: theme.spacing(1.5),
        flexShrink: 0,
        [theme.breakpoints.down('md')]: { display: 'none' },
    })),

    // A light border (no fill) gives this enough affordance to read as its own clickable control
    // next to the solid Create Order button, without going back to the heavier gray-filled pill.
    UserButton: styled(ButtonBase)(({ theme }) => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        gap: '8px',
        padding: '4px 10px 4px 4px',
        borderRadius: '999px',
        border: '1px solid #E2E8F0',
        backgroundColor: 'transparent',
        flexShrink: 0,
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': { backgroundColor: '#F0FAFB', borderColor: HEADER_ACCENT },
        [theme.breakpoints.down('md')]: { display: 'none' },
    })),

    // Aug 2026 - a soft tinted "chip" (light teal bg, dark teal initials) instead of a solid-fill
    // circle - softer, friendlier avatar treatment consistent with the rest of the modernized
    // header, and doubles as another (small) accent touch in the row.
    UserAvatar: styled(Box)({
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: '#CFFAFE',
        color: HEADER_ACCENT_HOVER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: 700,
        flexShrink: 0,
    }),

    // Truncates instead of forcing the header wider than the viewport (see NavigationContainer's
    // comment above) - this is what was actually clipping the name in practice, since a name has
    // no natural wrap/shrink point of its own.
    UserName: styled('span')(({ theme }) => ({
        fontWeight: 600,
        fontSize: '13px',
        color: '#1A2332',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '160px',
        // Matches NavigationLink's icon-only cutoff ('xl') - tightens the name earlier since more
        // primary nav items are now competing for the same row.
        [theme.breakpoints.down('xl')]: {
            maxWidth: '90px',
        },
    })),

    MenuHeader: styled(Box)({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        padding: '18px 16px',
        background: 'linear-gradient(135deg, #F0FAFB 0%, #F8FAFC 100%)',
        borderBottom: '1px solid #E2E8F0',
        overflow: 'hidden',
    }),

    MenuAvatar: styled(Box)({
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: '#CFFAFE',
        color: HEADER_ACCENT_HOVER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '15px',
        fontWeight: 700,
        flexShrink: 0,
        fontFamily: '"Inter", sans-serif',
        boxShadow: `0 0 0 3px #FFFFFF, 0 0 0 4.5px rgba(8,145,178,0.3)`,
    }),

    MenuHeaderName: styled('div')({
        fontWeight: 700,
        fontSize: '14px',
        color: '#0D1B2A',
        fontFamily: '"Inter", sans-serif',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),

    MenuHeaderMeta: styled('div')({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const,
        color: HEADER_ACCENT_HOVER,
        backgroundColor: 'rgba(8,145,178,0.12)',
        borderRadius: '20px',
        padding: '2px 8px',
        margin: '4px 0 3px',
        fontFamily: '"Inter", sans-serif',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'fit-content',
    }),

    MenuHeaderOrg: styled('div')({
        fontSize: '11.5px',
        fontWeight: 600,
        color: '#374151',
        fontFamily: '"Inter", sans-serif',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.4,
    }),

    MenuHeaderEmail: styled('div')({
        fontSize: '11px',
        color: '#6B7B8D',
        fontFamily: '"Inter", sans-serif',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1.4,
    }),

    // Aug 2026 - Test Credits balance, its own section in the user menu (below the profile
    // header, above My Account/Sign Out) - only rendered when the org actually has a Test
    // Credit balance to show.
    MenuCreditSection: styled(Box)({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '12px 16px',
        borderBottom: '1px solid #F1F5F9',
    }),

    MenuCreditLabel: styled('div')({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#374151',
        fontFamily: '"Inter", sans-serif',
    }),

    MenuCreditValue: styled('div')({
        fontSize: '13px',
        fontWeight: 700,
        color: '#059669',
        fontFamily: '"Inter", sans-serif',
    }),

    Logo: styled('img')(({ theme }) => ({
        width: 'auto',
        display: 'block',
        [theme.breakpoints.down('sm')]: {
            height: '38px',
        },
    })),

    HamburgerButton: styled(IconButton)(({ theme }) => ({
        marginLeft: 'auto',
        alignSelf: 'center',
        color: '#4F4F4F',
        display: 'none',
        [theme.breakpoints.down('md')]: {
            display: 'flex',
        },
    })),

    DrawerContent: styled(Box)({
        width: '280px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
    }),

    DrawerHeader: styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
        borderBottom: '1px solid rgba(8,145,178,0.2)',
    })),

    DrawerUserInfo: styled(Box)(({ theme }) => ({
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        borderBottom: '1px solid #F0F5F7',
        backgroundColor: '#F8FAFB',
    })),

    DrawerUserOrg: styled('div')({
        fontWeight: 700,
        fontSize: '14px',
        color: '#1C2B3A',
    }),

    DrawerUserEmail: styled('div')({
        fontSize: '12px',
        color: '#6B7B8D',
        marginTop: '2px',
    }),

    DrawerNavList: styled(Box)({
        flex: 1,
        overflowY: 'auto',
        paddingTop: '8px',
        paddingBottom: '8px',
    }),

    // Mirrors the desktop primary/"More" split so the grouping stays meaningful on mobile too,
    // rather than one long undifferentiated list.
    DrawerNavSectionLabel: styled('div')({
        padding: '14px 24px 6px',
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#94A3B8',
    }),

    DrawerNavLink: styled(RouterLink)<UINavigationLinkProps>(({ theme, selected }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: selected ? 700 : 500,
        color: selected ? HEADER_ACCENT : '#4F4F4F',
        borderLeft: selected ? `3px solid ${HEADER_ACCENT}` : '3px solid transparent',
        backgroundColor: selected ? 'rgba(8,145,178,0.06)' : 'transparent',
        transition: 'all 0.12s ease',
        '&:hover': {
            backgroundColor: 'rgba(8,145,178,0.06)',
            color: HEADER_ACCENT,
        },
    })),
};

ui.Logo.defaultProps = {
    src: '/images/SimplyTest-logo-2026.png',
    height: '48px',
};

ui.Header.defaultProps = {
    elevation: 2,
};

ui.NavigationContainer.defaultProps = {
    direction: 'row',
    spacing: 2,
};

export { ui };
